/**
 * Compare deux versions produit (semver souple : préfixe `v`, pré-release ignoré).
 *
 * Ligne commerciale publique : **1.1.x et suivantes** (octobre 2026).
 * Elle est plus récente que la série interne **2.3.x**, pour que le numéro
 * client « 1.1 » remplace 2.3.36 sans paraître comme un retour en arrière.
 * À l’intérieur d’une même ligne, l’ordre reste numérique (1.1.0 < 1.1.1).
 */
export function compareAppVersions(a: string, b: string): number {
  const pa = parseVersionParts(a);
  const pb = parseVersionParts(b);
  const aCom = isCommercialPublicLine(pa);
  const bCom = isCommercialPublicLine(pb);
  const aLegacy = isLegacyInternalLine(pa);
  const bLegacy = isLegacyInternalLine(pb);
  if (aCom && bLegacy) return 1;
  if (aLegacy && bCom) return -1;
  return compareNumericParts(pa, pb);
}

function parseVersionParts(raw: string): number[] {
  const core = raw
    .trim()
    .replace(/^v/i, '')
    .split(/[-+]/)[0] ?? '0';
  return core.split('.').map((p) => {
    const n = Number.parseInt(p.replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) ? n : 0;
  });
}

/** 1.1.0+ = numérotation commerciale client. */
function isCommercialPublicLine(parts: number[]): boolean {
  return (parts[0] ?? 0) === 1 && (parts[1] ?? 0) >= 1;
}

/** 2.x = ancienne numérotation interne (industrialisation). */
function isLegacyInternalLine(parts: number[]): boolean {
  return (parts[0] ?? 0) === 2;
}

function compareNumericParts(pa: number[], pb: number[]): number {
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
}

export const SHA256_HEX = /^[0-9a-f]{64}$/i;

export function isSha256Hex(value: string | undefined | null): boolean {
  return typeof value === 'string' && SHA256_HEX.test(value.trim());
}
